import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import { dialogs } from "../../context/DialogContext";
import api from "../../services/api";
import BuscadorPipelineComprador from "./BuscadorPipelineComprador";
import BuscadorClienteExpediente from "./BuscadorClienteExpediente";
import CamposNotariaParteExpediente from "./CamposNotariaParteExpediente";
import { compradorYaEnExpediente, nombreCompradorDesdeLead } from "../../utils/aplicarCompradorPipeline";
import {
  FORM_COMPRADOR_VACIO,
  datosNotariaDesdeCliente,
  formularioCompradorInicial,
  fusionarNotariaConPartesExistentes,
} from "../../utils/parteExpedienteCampos";

export default function ModalAnadirCompradorExpediente({ isOpen, onClose, onConfirm, compradoresActuales = [] }) {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#ffffff" : t.textPrimary;

  const [form, setForm] = useState(() => formularioCompradorInicial(compradoresActuales));

  useEffect(() => {
    if (isOpen) setForm(formularioCompradorInicial(compradoresActuales));
  }, [isOpen]);

  if (!isOpen) return null;

  const reset = () => setForm(formularioCompradorInicial(compradoresActuales));

  const inp = {
    padding: "6px 8px", borderRadius: 6, fontSize: 12, width: "100%",
    background: isDark ? "#1a2030" : t.inputBg,
    border: `1px solid ${isDark ? "#5a6a80" : t.inputBorder}`,
    color: tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  };

  const sel = { ...inp, cursor: "pointer" };
  const pctDef = compradoresActuales.length === 0 ? "100" : "";
  const lbl = { fontSize: 10, fontWeight: 700, opacity: 0.75, display: "block", marginBottom: 3 };

  const rellenarDesdeCliente = async (clienteId, base) => {
    if (!clienteId) return base;
    try {
      const r = await api.get(`/clientes/${clienteId}`);
      const c = r.data?.data || r.data;
      if (!c) return base;
      return { ...base, ...datosNotariaDesdeCliente(c) };
    } catch {
      return base;
    }
  };

  const seleccionarPipeline = async (lead) => {
    if (compradorYaEnExpediente(compradoresActuales, lead)) {
      dialogs.alert("Este comprador ya está en el expediente.");
      return;
    }
    const { nombre, apellidos } = nombreCompradorDesdeLead(lead);
    let next = {
      ...FORM_COMPRADOR_VACIO,
      lead_id: lead.lead_id || null,
      cliente_id: lead.cliente_id || null,
      nombre,
      apellidos,
      porcentaje: pctDef,
      telefono: lead.telefono || "",
      email: lead.email || "",
      whatsapp: lead.whatsapp || "",
      _lead: lead,
    };
    next = await rellenarDesdeCliente(lead.cliente_id, next);
    setForm(fusionarNotariaConPartesExistentes(next, compradoresActuales));
  };

  const seleccionarCliente = (cl) => {
    if (compradoresActuales.some((c) => c.cliente_id === cl.id)) {
      dialogs.alert("Este cliente ya está como comprador.");
      return;
    }
    const extra = datosNotariaDesdeCliente(cl);
    setForm(fusionarNotariaConPartesExistentes({
      ...FORM_COMPRADOR_VACIO,
      cliente_id: cl.id,
      nombre: cl.nombre || "",
      apellidos: cl.apellido || "",
      porcentaje: pctDef,
      telefono: cl.telefono || "",
      email: cl.email || "",
      whatsapp: cl.whatsapp || "",
      direccion: extra.direccion || "",
      nacionalidad: extra.nacionalidad || "",
    }, compradoresActuales));
  };

  const payloadComprador = () => ({
    lead_id: form.lead_id,
    cliente_id: form.cliente_id,
    nombre: form.nombre.trim(),
    apellidos: form.apellidos.trim(),
    porcentaje: form.porcentaje,
    telefono: form.telefono,
    email: form.email,
    whatsapp: form.whatsapp,
    direccion: form.direccion.trim(),
    estado_civil: form.estado_civil,
    regimen_matrimonial: form.regimen_matrimonial,
    profesion: form.profesion.trim(),
    sexo: form.sexo,
    nacionalidad: form.nacionalidad.trim(),
  });

  const confirmar = () => {
    if (!form.nombre?.trim() && !form.apellidos?.trim()) {
      dialogs.alert("Indica nombre o apellidos del comprador.");
      return;
    }
    const comprador = payloadComprador();
    if (form._lead) {
      onConfirm({ tipo: "pipeline", lead: form._lead, comprador });
    } else {
      onConfirm({ tipo: "manual", comprador });
    }
    reset();
    onClose();
  };

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 12000, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: 520, maxWidth: "94vw", maxHeight: "90vh", overflowY: "auto",
        background: isDark ? "#1e2330" : "#fff",
        borderRadius: 10, padding: 16, border: `1px solid ${isDark ? "#4a5568" : t.border}`, color: tx,
      }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>Añadir comprador</div>
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, opacity: 0.8 }}>Buscar en pipeline</div>
        <BuscadorPipelineComprador isDark={isDark} tx={tx} onSeleccionar={seleccionarPipeline} />
        <div style={{ fontSize: 11, fontWeight: 700, margin: "10px 0 4px", opacity: 0.8 }}>Buscar cliente CRM</div>
        <BuscadorClienteExpediente isDark={isDark} onSeleccionar={seleccionarCliente} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 72px", gap: 8, marginTop: 12 }}>
          <div>
            <label style={lbl}>Nombre</label>
            <input style={inp} value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
          </div>
          <div>
            <label style={lbl}>Apellidos</label>
            <input style={inp} value={form.apellidos} onChange={(e) => setForm((f) => ({ ...f, apellidos: e.target.value }))} />
          </div>
          <div>
            <label style={lbl}>%</label>
            <input style={{ ...inp, textAlign: "right" }} value={form.porcentaje} onChange={(e) => setForm((f) => ({ ...f, porcentaje: e.target.value }))} placeholder="100" />
          </div>
        </div>
        <CamposNotariaParteExpediente form={form} setForm={setForm} inp={inp} sel={sel} lbl={lbl} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button type="button" onClick={() => { reset(); onClose(); }}
            style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: tx, cursor: "pointer" }}>
            Cancelar
          </button>
          <button type="button" onClick={confirmar}
            style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
            Añadir
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
